import { Angle, DisplayUnitPerDimensionlessMetrics, Distance, ElectricalConductivity, HeatAccumulation, Length, Percent, PhotosyntheticActiveRadiation, PhysicalQuantityPerMetric, Pressure, RainfallIntensity, Speed, SurfacePowerDensity, Temperature, Time, Voltage, isMetricWithUnit } from '../../enums/UnitChoices';
import { availableConverters, defaultConverter, toDefaultUnitConverters } from './converters';
const availableUnits = {
    electricalConductivity: {
        [ElectricalConductivity.milliSiemensPerMeter]: 'millisiemens_per_meter',
        [ElectricalConductivity.deciSiemensPerMeter]: 'decisiemens_per_meter'
    },
    temperature: {
        [Temperature.celsiusDegrees]: 'degrees_celsius',
        [Temperature.fahrenheitDegrees]: 'degrees_fahrenheit',
        [Temperature.kelvins]: 'kelvins'
    },
    length: {
        [Length.millimeters]: 'millimeters',
        [Length.inches]: 'inches'
    },
    windSpeed: {
        [Speed.kilometersPerHour]: 'kilometers_per_hour',
        [Speed.metersPerSecond]: 'meters_per_second',
        [Speed.milesPerHour]: 'miles_per_hour',
        [Speed.beaufort]: 'beaufort'
    },
    percent: {
        [Percent.percent]: 'percent'
    },
    degrees: {
        [Angle.degrees]: 'degrees'
    },
    pressure: {
        [Pressure.kiloPascals]: 'kilopascals'
    },
    heatAccumulation: {
        [HeatAccumulation.celsiusDegreesDay]: 'celsius_degrees_day',
        [HeatAccumulation.fahrenheitDegreesDay]: 'fahrenheit_degrees_day'
    },
    leafWetness: {
        //same since at some point back was sending 0 as a dev mistake
        // now that this is fix both are converted the same way during transition
        [Voltage.volts]: 'lws_intensity_level',
        [Voltage.millivolts]: 'lws_intensity_level'
    },
    leafWetnessDuration: {
        [Time.seconds]: 'seconds'
    },
    rainfallIntensity: {
        [RainfallIntensity.millimetersPerHour]: 'millimeters_per_hour',
        [RainfallIntensity.inchesPerHour]: 'inches_per_hour'
    },
    voltage: {
        [Voltage.volts]: 'volts',
        [Voltage.millivolts]: 'millivolts'
    },
    surfacePowerDensity: {
        [SurfacePowerDensity.wattsPerSquareMeter]: 'watts_per_square_meter'
    },
    time: {
        [Time.seconds]: 'seconds',
        [Time.hours]: 'hours'
    },
    photosyntheticActiveRadiation: {
        [PhotosyntheticActiveRadiation.photosyntheticPhotonFluxDensity]: 'photosynthetic_photon_flux_density'
    },
    distance: {
        [Distance.kilometers]: 'kilometers',
        [Distance.miles]: 'miles'
    }
};
const hoursOnlyMetrics = ['COLD_T_HOUR', 'SSI_HOURS', 'SSI_HOURSAccumulation'];
export const getUnits = (metric) => availableUnits[PhysicalQuantityPerMetric[metric]];
export const getDefaultUnit = (metric) => {
    if (isMetricWithUnit(metric)) {
        if (hoursOnlyMetrics.includes(metric)) {
            return 'hours';
        }
        else {
            return availableUnits[PhysicalQuantityPerMetric[metric]][0];
        }
    }
    else {
        return DisplayUnitPerDimensionlessMetrics[metric];
    }
};
/**
 * this method return user preferred unit
 * or fallback to default one if preferredUnits is undefined
 */
export const getMyUnit = (metric, preferredUnits) => {
    let result = getDefaultUnit(metric);
    if (preferredUnits && isMetricWithUnit(metric)) {
        let unitToUse = preferredUnits[metric];
        const physicalQuantity = PhysicalQuantityPerMetric[metric];
        //if it's a temperature with use temperature
        if (metric === 'heatAccumulation' || physicalQuantity === 'temperature') {
            unitToUse = preferredUnits['T'];
            //kelvins not exist in heat accumulation
            if (metric === 'heatAccumulation' && unitToUse === Temperature.kelvins) {
                unitToUse = Temperature.celsiusDegrees;
            }
        }
        // basing rainfall intensity on rainfall preferences choices
        else if (metric === 'rainfallIntensity' || metric === 'RRAccumulation') {
            if (preferredUnits['RR'] === Length.millimeters) {
                unitToUse = RainfallIntensity.millimetersPerHour;
            }
            else {
                unitToUse = RainfallIntensity.inchesPerHour;
            }
        }
        else if (physicalQuantity === 'windSpeed') {
            unitToUse = preferredUnits['FF'];
        }
        else if (physicalQuantity === 'length') {
            unitToUse = preferredUnits['RR'];
        }
        else if (metric === 'PPFD' || metric === 'PPFD_MAX' || metric === 'PPFD_MIN') {
            unitToUse = PhotosyntheticActiveRadiation.photosyntheticPhotonFluxDensity;
        }
        else if (metric === 'SSI' || metric === 'SSI_MAX' || metric === 'SSI_MIN') {
            unitToUse = SurfacePowerDensity.wattsPerSquareMeter;
        }
        else if (hoursOnlyMetrics.includes(metric)) {
            unitToUse = Time.hours;
        }
        else if (metric === 'ECP') {
            if ('ECP' in preferredUnits) {
                unitToUse = preferredUnits['ECP'];
            }
            else {
                unitToUse = ElectricalConductivity.deciSiemensPerMeter;
            }
        }
        else if (metric === 'SWC') {
            unitToUse = Percent.percent;
        }
        else if (metric === 'VPD') {
            unitToUse = Pressure.kiloPascals;
        }
        else if (metric === 'distance') {
            unitToUse = preferredUnits['distance'] ?? Distance.kilometers;
        }
        result = getUnits(metric)[unitToUse];
    }
    return result;
};
export const getConverters = (metric) => {
    if (isMetricWithUnit(metric) && !hoursOnlyMetrics.includes(metric)) {
        return availableConverters[PhysicalQuantityPerMetric[metric]];
    }
    else {
        return defaultConverter;
    }
};
export const getToDefaultUnitConverters = (metric) => {
    if (isMetricWithUnit(metric) && !hoursOnlyMetrics.includes(metric)) {
        return toDefaultUnitConverters[PhysicalQuantityPerMetric[metric]];
    }
    else {
        return defaultConverter;
    }
};
export const getMetricsPerPhysicalQuantity = (pq) => {
    const matchingMetrics = [];
    Object.entries(PhysicalQuantityPerMetric).forEach(([metric, physicalQuantity]) => {
        if (physicalQuantity === pq) {
            matchingMetrics.push(metric);
        }
    });
    return matchingMetrics;
};
